import React, { useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { panelIcons, getPanelIconEntryPath, getPanelIconLabel } from '../../data/panelIconCatalog';
import { filterLauncherIcons } from './helpers';

/**
 * Built-in icon picker over generated panelIcons.json (curated PNG library).
 * Search and selection visuals match HomeLauncher ButtonAdminModal.
 *
 * @param {object} props
 * @param {string} props.value - selected icon key (catalog id)
 * @param {(iconKey: string) => void} props.onChange
 * @param {Array<{id:string,label?:string,filename?:string,category?:string}>} [props.icons]
 * @param {string} [props.search]
 * @param {(search: string) => void} [props.onSearchChange]
 * @param {string} [props.helperText]
 */
export default function IconPicker({
  value,
  onChange,
  icons = panelIcons,
  search: controlledSearch,
  onSearchChange,
  helperText = 'Choose an icon for this button. You can also use a color + text style instead.',
}) {
  const [internalSearch, setInternalSearch] = useState('');
  const isControlled = controlledSearch !== undefined;
  const iconSearch = isControlled ? controlledSearch : internalSearch;

  const setIconSearch = (next) => {
    if (isControlled) {
      onSearchChange?.(next);
    } else {
      setInternalSearch(next);
    }
  };

  const filteredIcons = useMemo(
    () => filterLauncherIcons(icons, iconSearch),
    [icons, iconSearch]
  );

  return (
    <Stack spacing={1}>
      <TextField
        label="Search icons"
        size="small"
        value={iconSearch}
        onChange={(e) => setIconSearch(e.target.value)}
      />
      <Box
        sx={{
          maxHeight: 260,
          overflowY: 'auto',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          p: 1,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
            gap: 1,
          }}
        >
          {filteredIcons.map((it) => {
            const label = getPanelIconLabel(it);
            return (
              <Box
                key={it.id}
                onClick={() => onChange?.(it.id)}
                title={label}
                sx={{
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: value === it.id ? 'primary.main' : 'transparent',
                  bgcolor: value === it.id ? 'action.selected' : 'background.paper',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={getPanelIconEntryPath(it)}
                  alt={label}
                  sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      {helperText ? (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
    </Stack>
  );
}
